'use client';

import { create } from 'zustand';
import { VenueData, loadVenueData, loadVenuePartial, ensureFullVenue } from '@/lib/venueData';
import { computeRoute, NavigationStep, getGatesForBuilding, generateSemanticSteps } from '@/lib/routeCalculator';
import buildingsMetadata from '../../data/venue/buildings_metadata.json';

export interface NavigationStoreState {
  data: VenueData | null;
  fromId: string | undefined;
  toId: string | undefined;
  currentFloorId: string;
  route: [number, number][] | undefined;
  highlighted: string[] | undefined;
  extStart: [number, number] | undefined;
  closedGates: string[];
  multiFloorRoutes: {
    leg1?: [number, number][];
    leg2?: [number, number][];
    fromFloorId?: string;
    toFloorId?: string;
  } | null;
  selectedGateId: string;
  steps: NavigationStep[];
  error: string | null;
  isLoading: boolean;
  isOffline: boolean;

  activeBuilding: string | null; // 'AB3' or null (for campus view)
  isFetchingBuilding: boolean;
  campusStyle: any | null;

  // Simulation State
  isSimulating: boolean;
  isSimPaused: boolean;
  simSpeed: number;
  simStepIndex: number;

  // Actions
  loadOutsideCampus: () => Promise<void>;
  enterBuilding: (buildingId: string) => Promise<void>;
  exitBuilding: () => void;
  handleNavigate: (fromId: string, toId: string) => Promise<void>;
  handleClear: () => void;
  handleGateToggle: (gateId: string) => Promise<void>;
  handleFloorChange: (floorId: string) => void;
  handleSpaceClick: (spaceId: string) => Promise<void>;
  startSimulation: () => void;
  stopSimulation: () => void;
  setSimPaused: (paused: boolean) => void;
  setSimSpeed: (speed: number) => void;
  setSimStepIndex: (idx: number) => void;
  setError: (msg: string | null) => void;
}

// Asynchronous computation wrapper (Web Worker prepared)
const calculateRouteAsync = (
  data: VenueData,
  fromId: string,
  toId: string,
  closedGates: string[],
  currentFloorId: string,
  buildingId: string
): Promise<any> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(computeRoute(data, fromId, toId, closedGates, currentFloorId, buildingId));
    }, 0);
  });
};

export const useNavigationStore = create<NavigationStoreState>((set, get) => ({
  data: null,
  fromId: undefined,
  toId: undefined,
  currentFloorId: 'm_fa5a3ee91d369d98',
  route: undefined,
  highlighted: undefined,
  extStart: undefined,
  closedGates: [],
  multiFloorRoutes: null,
  selectedGateId: 'main_1',
  steps: [],
  error: null,
  isLoading: false,
  isOffline: false,

  // Simulation State
  isSimulating: false,
  isSimPaused: false,
  simSpeed: 1,
  simStepIndex: 0,

  activeBuilding: null,
  isFetchingBuilding: false,
  campusStyle: null,

  setError: (msg: string | null) => set({ error: msg }),

  loadOutsideCampus: async () => {
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    if (!online) {
      set({ isOffline: true, error: 'Offline: Waiting for connection...' });
      return;
    }
    set({ isOffline: false, error: null });
  },

  enterBuilding: async (buildingId: string) => {
    const { activeBuilding, data } = get();
    if (activeBuilding === buildingId && data) return;

    set({ isFetchingBuilding: true, error: null });
    try {
      const bldMeta = (buildingsMetadata as any)[buildingId.toLowerCase()];
      const defaultFloor: string = bldMeta?.defaultFloor ?? bldMeta?.floors?.[0]?.id ?? 'm_fa5a3ee91d369d98';

      // Fast partial load for immediate rendering
      const partialVenue = await loadVenuePartial(buildingId, defaultFloor);
      set({
        data: partialVenue,
        activeBuilding: buildingId,
        currentFloorId: defaultFloor,
        isFetchingBuilding: false,
        error: null
      });

      // Background: load full graph for navigation (non-blocking)
      loadVenueData(buildingId).then(fullVenue => {
        const current = get();
        if (current.activeBuilding === buildingId) {
          set({ data: fullVenue });
        }
      }).catch(() => {});
    } catch (err) {
      console.error('Error lazy loading building data:', err);
      set({
        isFetchingBuilding: false,
        error: `Could not load indoor maps for building ${buildingId}`
      });
    }
  },

  exitBuilding: () => {
    set({ 
      activeBuilding: null, 
      data: null, 
      fromId: undefined, 
      toId: undefined, 
      route: undefined, 
      highlighted: undefined, 
      extStart: undefined, 
      multiFloorRoutes: null, 
      steps: [] 
    });
  },

  handleNavigate: async (fromId: string, toId: string) => {
    // If not in building, lazily enter AB3 building!
    const { activeBuilding, data } = get();
    if (!activeBuilding || !data) {
      await get().enterBuilding('AB3');
    }

    const currentData = get().data;
    if (!currentData) return;

    set({ isLoading: true, fromId, toId });
    // Cross-floor routing needs the complete node graph — resolve full venue (instant if cached)
    const routeData = await ensureFullVenue(activeBuilding || 'ab3');
    const res = await calculateRouteAsync(routeData, fromId, toId, get().closedGates, get().currentFloorId, activeBuilding || 'ab3');
    if (!res) {
      set({ 
        isLoading: false, 
        error: 'Could not calculate navigation route.' 
      });
      return;
    }

    set({
      fromId: res.fromId,
      toId: res.toId,
      route: res.route,
      highlighted: res.highlighted,
      extStart: res.extStart,
      selectedGateId: res.selectedGateId,
      multiFloorRoutes: res.multiFloorRoutes,
      steps: res.steps,
      currentFloorId: res.currentFloorId,
      error: null,
      isLoading: false,
    });
  },

  handleClear: () => {
    set({
      fromId: undefined,
      toId: undefined,
      route: undefined,
      highlighted: undefined,
      extStart: undefined,
      multiFloorRoutes: null,
      steps: [],
      error: null,
      isSimulating: false,
      isSimPaused: false,
      simStepIndex: 0,
    });
  },

  handleGateToggle: async (gateId: string) => {
    const { activeBuilding, data, fromId, toId, closedGates, currentFloorId } = get();
    const updatedClosedGates = closedGates.includes(gateId)
      ? closedGates.filter(id => id !== gateId)
      : [...closedGates, gateId];

    set({ closedGates: updatedClosedGates });

    if (data && fromId && toId) {
      set({ isLoading: true });
      const routeData = await ensureFullVenue(activeBuilding || 'ab3');
      const res = await calculateRouteAsync(routeData, fromId, toId, updatedClosedGates, currentFloorId, activeBuilding || 'ab3');
      if (res) {
        set({
          route: res.route,
          highlighted: res.highlighted,
          extStart: res.extStart,
          selectedGateId: res.selectedGateId,
          multiFloorRoutes: res.multiFloorRoutes,
          steps: res.steps,
          currentFloorId: res.currentFloorId,
        });
      }
      set({ isLoading: false });
    }
  },

  handleFloorChange: (floorId: string) => {
    const { activeBuilding, multiFloorRoutes, route, steps, data, fromId, toId, selectedGateId } = get();
    let nextRoute = route;

    if (multiFloorRoutes) {
      if (floorId === multiFloorRoutes.fromFloorId) {
        nextRoute = multiFloorRoutes.leg1;
      } else if (floorId === multiFloorRoutes.toFloorId) {
        nextRoute = multiFloorRoutes.leg2;
      } else {
        nextRoute = undefined;
      }
    }

    let nextSteps = steps;
    if (data && fromId && toId) {
      const toLoc = data.locations.find(l => l.id === toId);
      const fromLocName = fromId === 'EXTERNAL' ? 'EXTERNAL' : data.locations.find(l => l.id === fromId)?.name || '';
      const gates = getGatesForBuilding(activeBuilding || 'ab3');
      const activeGate = gates.find(g => g.id === selectedGateId) || gates[0];
      if (toLoc) {
        nextSteps = generateSemanticSteps(
          fromLocName,
          toLoc.name,
          activeGate.name,
          !!multiFloorRoutes,
          multiFloorRoutes?.fromFloorId || 'm_fa5a3ee91d369d98',
          toLoc.floorId,
          floorId
        );
      }
    }

    set({
      currentFloorId: floorId,
      route: nextRoute,
      steps: nextSteps,
    });
  },

  handleSpaceClick: async (spaceId: string) => {
    const { activeBuilding, data, closedGates, currentFloorId } = get();
    if (!activeBuilding || !data) {
      await get().enterBuilding('AB3');
    }

    const currentData = get().data;
    if (!currentData) return;

    // Find the location on the correct floor — prefer the floor currently shown
    const allLocs = currentData.locations.filter(l => l.spaceId === spaceId);
    if (allLocs.length === 0) return;

    // Pick the one on the current floor if possible, else the first
    const loc = allLocs.find(l => l.floorId === currentFloorId) || allLocs[0];

    // Always navigate from EXTERNAL (main entrance) to the tapped room
    set({ isLoading: true });
    const routeData = await ensureFullVenue(activeBuilding || 'ab3');
    const res = await calculateRouteAsync(
      routeData,
      'EXTERNAL',
      loc.id,
      closedGates,
      loc.floorId,
      activeBuilding || 'ab3'
    );

    if (res) {
      set({
        fromId: 'EXTERNAL',
        toId: loc.id,
        route: res.route,
        highlighted: res.highlighted,
        extStart: res.extStart,
        selectedGateId: res.selectedGateId,
        multiFloorRoutes: res.multiFloorRoutes,
        steps: res.steps,
        currentFloorId: res.currentFloorId,
        error: null,
        isLoading: false,
      });
    } else {
      // Fallback: just highlight the room without a route
      set({
        fromId: 'EXTERNAL',
        toId: loc.id,
        highlighted: [spaceId],
        route: undefined,
        extStart: undefined,
        multiFloorRoutes: null,
        steps: [],
        currentFloorId: loc.floorId,
        error: null,
        isLoading: false,
      });
    }
  },

  startSimulation: () => set({ isSimulating: true, isSimPaused: false, simSpeed: 1, simStepIndex: 0 }),
  stopSimulation: () => set({ isSimulating: false, isSimPaused: false, simStepIndex: 0 }),
  setSimPaused: (paused) => set({ isSimPaused: paused }),
  setSimSpeed: (speed) => set({ simSpeed: speed }),
  setSimStepIndex: (idx) => set({ simStepIndex: idx }),
}));
